import type { VNode } from 'vue';

/**
 * A CE node counts as real content if it carries any non-empty prop or text.
 * A node with only an `element` key (no props, no text, no populated child
 * slots) is an empty placeholder. Recurses the whole tree, including nested
 * CE slots (e.g. copyBlock / mediaBlockCopy).
 */
const nodeHasData = (vnode: any): boolean => {
  if (vnode == null || typeof vnode !== 'object') return false;

  // text node
  if (typeof vnode.children === 'string') return vnode.children.trim() !== '';

  // any meaningful prop value
  if (
    vnode.props &&
    Object.values(vnode.props).some(
      (v) => v !== null && v !== undefined && v !== '',
    )
  ) {
    return true;
  }

  // array children (incl. flattened fragments)
  if (Array.isArray(vnode.children) && vnode.children.some(nodeHasData)) {
    return true;
  }

  // slot functions (nested CE slots)
  if (vnode.children && typeof vnode.children === 'object') {
    for (const slot of Object.values(vnode.children)) {
      if (typeof slot === 'function') {
        const out = (slot as () => VNode | VNode[])();
        if ((Array.isArray(out) ? out : [out]).some(nodeHasData)) return true;
      }
    }
  }

  return false;
};

/**
 * Returns whether the named slot of the current component contains any real
 * content (not just empty CE element placeholders). Useful for hiding wrapper
 * sections when Drupal sends an empty paragraph.
 *
 * @example
 * const hasContent = useSlotHasContent('contentCopy');
 * // <Wrapper v-if="hasContent">…<slot name="contentCopy" /></Wrapper>
 */
export function useSlotHasContent(slotName: string) {
  const { getSlotItems } = useDrupalCe();
  const items = getSlotItems(slotName);
  return computed(() => items.value.some(nodeHasData));
}
